/*
 * Buying Journey: review visualisation, spec builder.
 *
 * Pure logic, no Three.js, no DOM. Turns journey state (measure + netting +
 * hardware + skywalks) into a plain scene description the renderer draws:
 *
 *   {
 *     bounds: { x, y, z },              // scene extents in metres
 *     ground: { w, d },
 *     walls: [{ x, y, z, w, h, d, material, colour, label }],
 *     posts: [{ x, z, h }],
 *     netPanels: [{ x, y, z, w, h, rotX, rotY, colour, label }],
 *     items: [{ kind, title, x, z, facing }],
 *     markers: [{ type: 'zip', x, y, z, rotY, label }],
 *     labels: [{ text, x, y, z }]
 *   }
 *
 * The scene is in whatever unit the store sells in, centred on the origin,
 * because the customer's own measurements go in untouched. Splitting this from
 * the renderer keeps it testable in jsdom, where WebGL does not exist, and
 * means the drawing layer stays dumb.
 *
 * Two kinds of number live in here and they must not be confused:
 *
 *   - Customer measurements (length, width, height, end sizes). Already in the
 *     store's unit. Never scaled.
 *   - Real-world constants (wall height and thickness, fence height, how far an
 *     item sits off a wall, label offsets). Written in metres because that is
 *     how they were reasoned about, and wrapped in m() so they come out in the
 *     store's unit.
 *
 * Get that backwards and nothing errors: you just get a balcony with a
 * 2.4-foot ceiling, or a 33-metre alley.
 */
(function () {
  'use strict';

  /*
   * Scene units per metre, set per build() call. Module-level rather than
   * threaded through every helper because build() is synchronous and runs once
   * per render, and `m(2.4)` at each call site reads far better than passing a
   * scale into a dozen functions.
   */
  var U = 3.28084;

  /** Metres to scene units. Wrap every real-world constant in this. */
  function m(metres) {
    return metres * U;
  }

  var WALL_HEIGHT_M = 2.4; // default alley wall height when nothing better known

  var NET_COLOURS = {
    black: '#2a2a2a',
    stone: '#b5ad9e',
    stainless: '#9ba1a6',
    white: '#f0f0ee'
  };

  var SURFACE_COLOURS = {
    wood: '#a97a4b',
    brick: '#a05a44',
    colorbond: '#7a8288',
    other: '#8f9296'
  };

  function surfaceColour(material) {
    return SURFACE_COLOURS[material] || SURFACE_COLOURS.other;
  }

  function netColour(colourId) {
    return NET_COLOURS[colourId] || NET_COLOURS.black;
  }

  /** Map a skywalks product handle to a primitive kind the renderer knows. */
  function itemKind(handle) {
    var h = String(handle || '').toLowerCase();

    if (h.indexOf('bridge') > -1) return 'bridge';
    if (h.indexOf('tower') > -1) return 'tower';
    if (h.indexOf('tree') > -1) return 'tree';
    if (h.indexOf('hammock') > -1) return 'hammock';
    if (h.indexOf('hideaway') > -1) return 'hideaway';
    if (h.indexOf('scratch') > -1) return 'scratcher';
    return 'generic';
  }

  /** Flatten skywalks selections into one entry per physical unit. */
  function itemUnits(skywalks) {
    var units = [];
    if (!skywalks || !skywalks.items) return units;

    for (var i = 0; i < skywalks.items.length; i++) {
      var line = skywalks.items[i];
      var qty = Math.max(1, line.quantity || 1);

      for (var q = 0; q < qty; q++) {
        units.push({
          kind: itemKind(line.handle),
          handle: line.handle,
          title: line.title
        });
      }
    }
    return units;
  }

  /** Spread items along a run, against the primary wall. */
  function placeItems(units, length, z, facing) {
    var placed = [];
    var count = units.length;
    if (!count) return placed;

    // Keep clear of the ends; spread the rest evenly.
    var usable = Math.max(length - m(1), m(0.5));
    var step = count > 1 ? usable / (count - 1) : 0;
    var start = -usable / 2;

    for (var i = 0; i < count; i++) {
      placed.push({
        kind: units[i].kind,
        title: units[i].title,
        x: count > 1 ? start + step * i : 0,
        z: z,
        facing: facing
      });
    }
    return placed;
  }

  function answers(hardware) {
    return (hardware && hardware.answers) || {};
  }

  function nettingLabel(netting, unit) {
    if (!netting) return '';
    if (netting.format === 'btm') {
      return netting.quantity + unit + ' of ' + netting.variantTitle + ' netting';
    }
    return netting.variantTitle + ' netting';
  }

  function build(journey, units) {
    var measure = journey && journey.measure;
    if (!measure || !measure.area || !measure.length) return null;

    U = units === 'm' ? 1 : 3.28084;
    var unit = units === 'm' ? 'm' : 'ft';
    var WALL_HEIGHT = m(WALL_HEIGHT_M);

    var netting = journey.netting || null;
    var hardware = journey.hardware || null;
    var skywalks = journey.skywalks || null;

    var a = answers(hardware);
    var colour = netColour(netting && netting.colour);
    var l = measure.length;

    var spec = {
      area: measure.area,
      bounds: { x: l, y: WALL_HEIGHT, z: m(3) },
      ground: { w: l + m(2), d: m(6) },
      walls: [],
      posts: [],
      netPanels: [],
      items: [],
      markers: [],
      labels: []
    };

    var netLabel = nettingLabel(netting, unit);

    if (measure.area === 'alley') {
      var w = measure.width || m(2);
      spec.bounds = { x: l, y: WALL_HEIGHT, z: w };
      spec.ground = { w: l + m(2), d: w + m(3) };

      // House side and fence side.
      spec.walls.push({
        x: 0, y: WALL_HEIGHT / 2, z: -w / 2, w: l, h: WALL_HEIGHT, d: m(0.12),
        material: a.m1 || 'other', colour: surfaceColour(a.m1), label: 'Side 1'
      });
      spec.walls.push({
        x: 0, y: WALL_HEIGHT / 2, z: w / 2, w: l, h: WALL_HEIGHT, d: m(0.12),
        material: a.m2 || 'other', colour: surfaceColour(a.m2), label: 'Side 2'
      });

      // Netting roof spanning the gap.
      spec.netPanels.push({
        x: 0, y: WALL_HEIGHT, z: 0, w: l, h: w,
        rotX: -90, rotY: 0, colour: colour, label: netLabel
      });

      // Enclosed ends become vertical net panels.
      var ends = [];
      if (measure.end1) ends.push({ end: measure.end1, x: -l / 2 });
      if (measure.end2) ends.push({ end: measure.end2, x: l / 2 });

      for (var e = 0; e < ends.length; e++) {
        spec.netPanels.push({
          x: ends[e].x, y: ends[e].end.height / 2, z: 0,
          w: ends[e].end.width, h: ends[e].end.height,
          rotX: 0, rotY: 90, colour: colour, label: 'End'
        });
      }

      // Zip marker sits on the first enclosed end, else on the first side.
      if (netting && netting.zip) {
        var zx = measure.end1 ? -l / 2 : 0;
        var zz = measure.end1 ? 0 : w / 2 - m(0.1);
        spec.markers.push({
          type: 'zip', x: zx, y: m(0.9), z: zz,
          rotY: measure.end1 ? 90 : 0, label: netting.zip.title
        });
      }

      spec.items = placeItems(itemUnits(skywalks), l, -w / 2 + m(0.45), 0);

      spec.labels.push({ text: l + unit, x: 0, y: m(0.02), z: w / 2 + m(0.8) });
      spec.labels.push({ text: w + unit, x: l / 2 + m(0.8), y: m(0.02), z: 0 });
    } else if (measure.area === 'balcony') {
      var h = measure.height || WALL_HEIGHT;
      var depth = m(2);
      spec.bounds = { x: l, y: h, z: depth };
      spec.ground = { w: l + m(2), d: depth + m(3) };

      // Floor and roof slabs, side walls, using the four surface answers.
      spec.walls.push({
        x: 0, y: m(0.06), z: 0, w: l, h: m(0.12), d: depth,
        material: a.m4 || 'other', colour: surfaceColour(a.m4), label: 'Floor'
      });
      spec.walls.push({
        x: 0, y: h, z: 0, w: l, h: m(0.12), d: depth,
        material: a.m3 || 'other', colour: surfaceColour(a.m3), label: 'Roof'
      });
      spec.walls.push({
        x: -l / 2, y: h / 2, z: 0, w: m(0.12), h: h, d: depth,
        material: a.m1 || 'other', colour: surfaceColour(a.m1), label: 'Side 1'
      });
      spec.walls.push({
        x: l / 2, y: h / 2, z: 0, w: m(0.12), h: h, d: depth,
        material: a.m2 || 'other', colour: surfaceColour(a.m2), label: 'Side 2'
      });

      // Netting drops down the open front.
      spec.netPanels.push({
        x: 0, y: h / 2, z: depth / 2, w: l, h: h,
        rotX: 0, rotY: 0, colour: colour, label: netLabel
      });

      spec.items = placeItems(itemUnits(skywalks), l, -depth / 2 + m(0.45), 0);

      spec.labels.push({ text: l + unit, x: 0, y: m(0.02), z: depth / 2 + m(0.8) });
      spec.labels.push({ text: h + unit, x: l / 2 + m(0.8), y: h / 2, z: depth / 2 });
    } else if (measure.area === 'fenceline') {
      var fh = m(1.8);
      var nh = measure.height || m(1.8);
      spec.bounds = { x: l, y: fh + nh * 0.7, z: m(2) };
      spec.ground = { w: l + m(2), d: m(5) };

      // The fence itself, in the fence material.
      spec.walls.push({
        x: 0, y: fh / 2, z: 0, w: l, h: fh, d: m(0.08),
        material: a.m1 || 'other', colour: surfaceColour(a.m1), label: 'Fence'
      });

      // Posts, evenly spaced from the posts answer.
      var postCount = parseInt(a.p, 10);
      if (isFinite(postCount) && postCount > 0) {
        var spacing = postCount > 1 ? l / (postCount - 1) : 0;
        for (var p = 0; p < postCount; p++) {
          spec.posts.push({
            x: postCount > 1 ? -l / 2 + spacing * p : 0,
            z: 0,
            h: fh + nh * 0.6
          });
        }
      }

      // Netting leans in from the fence top, floppy-top style.
      spec.netPanels.push({
        x: 0, y: fh + (nh * 0.5) / 2, z: -nh * 0.25, w: l, h: nh,
        rotX: -35, rotY: 0, colour: colour, label: netLabel
      });

      spec.items = placeItems(itemUnits(skywalks), l, m(0.6), 180);

      spec.labels.push({ text: l + unit, x: 0, y: m(0.02), z: m(1.4) });
      spec.labels.push({ text: nh + unit + ' net', x: l / 2 + m(0.8), y: fh + m(0.4), z: 0 });
    } else {
      // Backyard, or any future overhead area: a canopy over the space.
      var bw = measure.width || m(4);
      spec.bounds = { x: l, y: WALL_HEIGHT, z: bw };
      spec.ground = { w: l + m(2), d: bw + m(3) };

      spec.netPanels.push({
        x: 0, y: WALL_HEIGHT, z: 0, w: l, h: bw,
        rotX: -90, rotY: 0, colour: colour, label: netLabel
      });

      spec.items = placeItems(itemUnits(skywalks), l, 0, 0);

      spec.labels.push({ text: l + unit, x: 0, y: m(0.02), z: bw / 2 + m(0.8) });
      spec.labels.push({ text: bw + unit, x: l / 2 + m(0.8), y: m(0.02), z: 0 });
    }

    return spec;
  }

  var api = {
    build: build,
    itemKind: itemKind,
    netColour: netColour,
    surfaceColour: surfaceColour
  };

  if (typeof window !== 'undefined') {
    window.CatnetsVizSpec = api;
  }

  /* Node export for the test suite. */
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})();
